import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Check, ArrowRight, Video, Users, Star, Shield, Handshake, Move, X } from 'lucide-react';
import { FaYoutube, FaFacebook, FaInstagram, FaTiktok, FaLinkedin } from 'react-icons/fa';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import ThumbnailCropper from '../components/ThumbnailCropper';
import { LiveEventHeroCard } from '../components/LiveEventHeroCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const { user } = useAuth();
  const [coaches, setCoaches] = useState([]);
  const [displayedCoaches, setDisplayedCoaches] = useState([]);
  const [coachesLoading, setCoachesLoading] = useState(true);
  const [coachesVisible, setCoachesVisible] = useState(false);
  const [editingCoachPhoto, setEditingCoachPhoto] = useState(null);
  const [savingPosition, setSavingPosition] = useState(false);
  const [teaserVideos, setTeaserVideos] = useState([]);
  const [liveEventTeasers, setLiveEventTeasers] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  const [nextEvent, setNextEvent] = useState(null);

  const isAdmin = user?.role === 'admin';

  // Fisher-Yates shuffle algorithm
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Fetch coaches and teaser videos
  useEffect(() => {
    const fetchCoaches = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/coaches`);
        const allCoaches = response.data.coaches || [];
        setCoaches(allCoaches);
        setDisplayedCoaches(shuffleArray(allCoaches));
        setTimeout(() => setCoachesVisible(true), 100);
      } catch (error) {
        console.error('Failed to fetch coaches:', error);
      } finally {
        setCoachesLoading(false);
      }
    };
    const fetchTeasers = async () => {
      try {
        const [res1, res2] = await Promise.all([
          axios.get(`${API_URL}/api/teaser-videos`),
          axios.get(`${API_URL}/api/live-event-teasers`)
        ]);
        setTeaserVideos(res1.data.videos || []);
        setLiveEventTeasers(res2.data.videos || []);
      } catch { /* ignore */ }
    };
    const fetchNextEvent = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/events/upcoming`);
        const events = res.data.events || [];
        if (events.length > 0) setNextEvent(events[0]);
      } catch { /* ignore */ }
    };
    fetchCoaches();
    fetchTeasers();
    fetchNextEvent();
  }, []);

  const handleSavePhotoPosition = async (position) => {
    if (!editingCoachPhoto) return;
    setSavingPosition(true);
    try {
      await axios.put(`${API_URL}/api/admin/coaches/${editingCoachPhoto.id}`, {
        photo_position: position
      });
      // Update local state for both coaches arrays
      const updateCoach = (c) => c.id === editingCoachPhoto.id ? { ...c, photo_position: position } : c;
      setCoaches(prev => prev.map(updateCoach));
      setDisplayedCoaches(prev => prev.map(updateCoach));
      setEditingCoachPhoto(null);
    } catch (error) {
      console.error('Failed to save photo position:', error);
      alert('Failed to save position');
    } finally {
      setSavingPosition(false);
    }
  };

  const features = [
    {
      icon: Video,
      title: 'Video Critique',
      description: 'Submit your roping videos and get detailed feedback from a professional coach.'
    },
    {
      icon: Users,
      title: 'Private Community',
      description: 'Connect with fellow ropers in a respectful, focused learning environment.'
    },
    {
      icon: Star,
      title: 'Credit-Based Coaching',
      description: 'Pay only for the coaching you need. No wasted subscriptions.'
    },
    {
      icon: Shield,
      title: 'AI Training Assistant',
      description: 'Get quick answers to common questions while you wait for coach feedback.'
    }
  ];

  const pricingPlans = [
    {
      name: 'Monthly',
      price: '$25',
      period: '/month',
      description: 'Full platform access',
      features: [
        'Community access',
        'AI Training Assistant',
        'Free Training Video Library',
        'Marketplace access',
        'Critique history',
        'Elevated Credits purchased separately'
      ],
      cta: 'Subscribe',
      popular: false
    },
    {
      name: 'Annual',
      price: '$240',
      period: '/year',
      description: 'Save $60 (2 months free)',
      features: [
        'Community access',
        'AI Training Assistant',
        'Free Training Video Library',
        'Marketplace access',
        'Critique history',
        'Elevated Credits purchased separately'
      ],
      cta: 'Subscribe',
      popular: true
    }
  ];

  const creditBundles = [
    { name: '20 Credits', credits: 20, price: '$20', perCredit: '$1.00' },
    { name: '50 Credits', credits: 50, price: '$45', perCredit: '$0.90' },
    { name: '100 Credits', credits: 100, price: '$80', perCredit: '$0.80' }
  ];

  // Detect external embed URLs (Vimeo / YouTube) and return embed src
  const getEmbedInfo = (url) => {
    if (!url) return null;
    // Vimeo: https://vimeo.com/123456789 or https://player.vimeo.com/video/123456789
    const vimeoMatch = url.match(/(?:vimeo\.com\/(?:video\/)?|player\.vimeo\.com\/video\/)(\d+)/);
    if (vimeoMatch) {
      return { type: 'vimeo', id: vimeoMatch[1] };
    }
    // YouTube: youtu.be/ID, youtube.com/watch?v=ID, youtube.com/embed/ID, youtube.com/shorts/ID
    const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{6,})/);
    if (ytMatch) {
      return { type: 'youtube', id: ytMatch[1] };
    }
    return null;
  };

  const buildEmbedSrc = (embed, withSound) => {
    if (embed.type === 'vimeo') {
      // background=1 hides controls and autoplays muted; for sound, drop background and unmute
      if (withSound) {
        return `https://player.vimeo.com/video/${embed.id}?autoplay=1&muted=1&loop=1&autopause=0&controls=1`;
      }
      return `https://player.vimeo.com/video/${embed.id}?autoplay=1&muted=1&loop=1&background=1&autopause=0`;
    }
    if (embed.type === 'youtube') {
      if (withSound) {
        return `https://www.youtube.com/embed/${embed.id}?autoplay=1&mute=0&loop=1&playlist=${embed.id}&controls=1&rel=0`;
      }
      return `https://www.youtube.com/embed/${embed.id}?autoplay=1&mute=1&loop=1&playlist=${embed.id}&controls=0&rel=0&modestbranding=1&playsinline=1`;
    }
    return '';
  };

  const handlePlayVideo = (videoId) => {
    // Pause all native videos and mute them
    document.querySelectorAll('video[id^="teaser-vid-"], video[id^="live-vid-"]').forEach(v => {
      v.pause();
      v.muted = true;
      v.currentTime = 0;
    });

    if (playingId === videoId) {
      setPlayingId(null);
      return;
    }

    setPlayingId(videoId);

    // For native videos, unmute & play
    const vid = document.getElementById(videoId);
    if (vid && vid.tagName === 'VIDEO') {
      vid.muted = false;
      vid.currentTime = 0;
      vid.play();
    }
  };

  const socialLinks = [
    { icon: FaYoutube, url: 'https://www.youtube.com/@ElevatedRopingMedia', label: 'YouTube' },
    { icon: FaFacebook, url: 'https://www.facebook.com/people/Elevated-Roping-Media/61587848631031/', label: 'Facebook' },
    { icon: FaInstagram, url: 'https://www.instagram.com/elevatedropingmedia/', label: 'Instagram' },
    { icon: FaTiktok, url: 'https://www.tiktok.com/@elevated296', label: 'TikTok' },
    { icon: FaLinkedin, url: 'https://www.linkedin.com/company/elevated-roping-media-inc/', label: 'LinkedIn' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section — Centered */}
      <section className="relative overflow-hidden bg-gradient-to-b from-background to-muted/50 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-fade-in max-w-4xl mx-auto">
            <h1 className="font-display font-black text-5xl md:text-7xl tracking-tight uppercase text-foreground leading-none">
              Elevate
              <span className="block text-primary">Every Run</span>
            </h1>

            {/* In-your-face live event card (only renders when a live event is on) */}
            <LiveEventHeroCard />

            <p className="font-serif text-xl md:text-2xl text-muted-foreground mt-6 leading-relaxed max-w-2xl mx-auto">
              No matter your age or number, there's always another level. Train smarter. Professional coaching built for ropers who compete to win.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-10 justify-center">
              <Link to="/signup">
                <Button 
                  size="lg" 
                  className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-8 py-6 text-lg font-bold uppercase tracking-wider shadow-lg hover:shadow-xl transition-all active:scale-95 w-full sm:w-auto"
                  data-testid="hero-cta-btn"
                >
                  Get Started
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="rounded-full px-8 py-6 text-lg font-bold uppercase tracking-wide border-2 w-full sm:w-auto"
                >
                  Sign In
                </Button>
              </Link>
            </div>
          </div>

          {/* Teaser Videos */}
          <div className="mt-16">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 max-w-7xl mx-auto">
              {[0, 1, 2, 3, 4, 5].map(i => {
                const teaser = teaserVideos[i];
                const vidId = `teaser-vid-${i}`;
                const isPlaying = playingId === vidId;
                const embed = getEmbedInfo(teaser?.url);
                return (
                  <div key={i} className="relative aspect-[9/16] rounded-xl overflow-hidden shadow-lg bg-muted" data-testid={`teaser-video-${i}`}>
                    {teaser?.url ? (
                      <>
                        {embed ? (
                          <iframe
                            id={vidId}
                            key={`${vidId}-${isPlaying ? 'sound' : 'muted'}`}
                            src={buildEmbedSrc(embed, isPlaying)}
                            className="absolute inset-0 w-full h-full"
                            frameBorder="0"
                            allow="autoplay; fullscreen; picture-in-picture; encrypted-media; web-share; clipboard-write"
                            allowFullScreen
                            title={teaser.title || `Teaser ${i + 1}`}
                          />
                        ) : (
                          <video id={vidId} src={teaser.url} className="w-full h-full object-cover" muted loop playsInline autoPlay />
                        )}
                        <button
                          className={`absolute inset-0 flex items-center justify-center ${isPlaying && embed ? 'pointer-events-none' : 'z-10'}`}
                          onClick={() => handlePlayVideo(vidId)}
                          data-testid={`teaser-play-${i}`}
                        >
                          {!isPlaying && (
                            <div className="w-14 h-14 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="white"><polygon points="6,3 20,12 6,21"/></svg>
                            </div>
                          )}
                        </button>
                        {isPlaying && (
                          <button
                            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors z-20"
                            onClick={(e) => { e.stopPropagation(); handlePlayVideo(vidId); }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                          </button>
                        )}
                        {teaser.title && (
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 pointer-events-none">
                            <p className="text-white text-sm font-display font-bold uppercase tracking-wide">{teaser.title}</p>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-muted/50">
                        <div className="text-center p-4">
                          <Video className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                          <p className="text-xs text-muted-foreground/50">Coming Soon</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* CTA below teasers */}
          <div className="mt-10 max-w-2xl mx-auto text-center">
            <p className="text-lg text-muted-foreground mb-4">
              Want access to our full video library? Get started with a monthly or annual subscription.
            </p>
            <Link to="/signup">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-10 py-6 text-lg font-bold uppercase tracking-wider shadow-lg hover:shadow-xl transition-all" data-testid="teaser-cta-btn">
                Get Started
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Live Event Teasers */}
      <section className="py-16 bg-muted/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-display font-bold text-3xl md:text-4xl uppercase tracking-tight">Live Event Highlights</h2>
            <p className="text-muted-foreground mt-2">Catch the action from our latest events</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto">
            {[0, 1, 2, 3, 4, 5].map(i => {
              const teaser = liveEventTeasers[i];
              const vidId = `live-vid-${i}`;
              const isPlaying = playingId === vidId;
              const embed = getEmbedInfo(teaser?.url);
              return (
                <div key={i} className="relative aspect-video rounded-xl overflow-hidden shadow-lg bg-muted" data-testid={`live-teaser-${i}`}>
                  {teaser?.url ? (
                    <>
                      {embed ? (
                        <iframe
                          id={vidId}
                          key={`${vidId}-${isPlaying ? 'sound' : 'muted'}`}
                          src={buildEmbedSrc(embed, isPlaying)}
                          className="absolute inset-0 w-full h-full"
                          frameBorder="0"
                          allow="autoplay; fullscreen; picture-in-picture; encrypted-media; web-share; clipboard-write"
                          allowFullScreen
                          title={teaser.title || `Live Event ${i + 1}`}
                        />
                      ) : (
                        <video id={vidId} src={teaser.url} className="w-full h-full object-cover" muted loop playsInline autoPlay />
                      )}
                      <button
                        className={`absolute inset-0 flex items-center justify-center ${isPlaying && embed ? 'pointer-events-none' : 'z-10'}`}
                        onClick={() => handlePlayVideo(vidId)}
                        data-testid={`live-play-${i}`}
                      >
                        {!isPlaying && (
                          <div className="w-16 h-16 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="white"><polygon points="6,3 20,12 6,21"/></svg>
                          </div>
                        )}
                      </button>
                      {isPlaying && (
                        <button
                          className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors z-20"
                          onClick={(e) => { e.stopPropagation(); handlePlayVideo(vidId); }}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                        </button>
                      )}
                      {teaser.title && (
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 pointer-events-none">
                          <p className="text-white text-sm font-display font-bold uppercase tracking-wide">{teaser.title}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-muted/50">
                      <div className="text-center p-4">
                        <Video className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                        <p className="text-xs text-muted-foreground/50">Coming Soon</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Upcoming Events CTA */}
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-black text-3xl md:text-5xl uppercase tracking-tight text-foreground">
            Don't Miss <span className="text-primary">Upcoming Events</span>
          </h2>
          <p className="font-serif text-lg md:text-xl text-muted-foreground mt-4 max-w-2xl mx-auto">
            Subscribe to get notified about clinics, jackpots, and live events near you.
          </p>
          <div className="mt-8">
            <Link to="/signup">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-10 py-6 text-lg font-bold uppercase tracking-wider shadow-lg hover:shadow-xl transition-all active:scale-95"
                data-testid="upcoming-events-cta-btn"
              >
                Get Started
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>

          {nextEvent && (
            <Link
              to="/events"
              className="mt-10 inline-block group"
              data-testid="next-event-link"
            >
              <div className="border-2 border-primary/30 hover:border-primary rounded-2xl px-6 py-5 bg-muted/30 hover:bg-muted/50 transition-all">
                <p className="text-xs font-display font-bold uppercase tracking-widest text-primary mb-2">Next Up</p>
                <p className="font-display font-bold text-xl md:text-2xl text-foreground group-hover:text-primary transition-colors">
                  {nextEvent.title}
                </p>
                {nextEvent.event_date ? (
                  <p className="text-sm text-muted-foreground mt-1">
                    {new Date(nextEvent.event_date + 'T12:00:00').toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      timeZone: 'America/Edmonton'
                    })}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground mt-1">Date TBA</p>
                )}
                <p className="text-sm text-primary font-bold uppercase tracking-wide mt-3 inline-flex items-center gap-1">
                  View All Events
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </p>
              </div>
            </Link>
          )}
        </div>
      </section>

      {/* Meet the Coaches — Horizontal */}
      <section className="py-16 bg-muted/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-display font-bold text-3xl md:text-4xl uppercase tracking-tight">Meet Your Coaches</h2>
            <p className="text-muted-foreground mt-2">Learn from proven professionals who compete at the highest level</p>
          </div>
          {coachesLoading ? (
            <div className="flex gap-4 overflow-hidden">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex-shrink-0 w-48 h-64 rounded-xl bg-muted animate-pulse" />
              ))}
            </div>
          ) : displayedCoaches.length > 0 ? (
            <div className={`flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide transition-opacity duration-700 ${coachesVisible ? 'opacity-100' : 'opacity-0'}`}>
              {displayedCoaches.map((coach, index) => (
                <div key={coach.id} className="relative group flex-shrink-0 w-44 sm:w-52 snap-start">
                  <Link to={`/coaches/${coach.id}`}>
                    <div 
                      className="aspect-[3/4] rounded-xl overflow-hidden shadow-lg relative animate-fade-in"
                      style={{ animationDelay: `${index * 80}ms` }}
                    >
                      <img 
                        src={coach.photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(coach.name)}&size=300&background=d4a574&color=fff&bold=true`} 
                        alt={coach.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        style={{ objectPosition: coach.photo_position?.objectPosition || 'center 20%' }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                        <p className="font-display font-bold text-sm uppercase tracking-wide">{coach.name}</p>
                        <p className="text-xs text-white/80 line-clamp-1 mt-0.5">
                          {coach.bio ? coach.bio.split('.')[0] + '.' : 'Professional Coach'}
                        </p>
                      </div>
                    </div>
                  </Link>
                  {isAdmin && coach.photo_url && (
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditingCoachPhoto(coach); }}
                      className="absolute top-2 right-2 bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
                      title="Adjust photo position"
                    >
                      <Move className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : null}
          <div className="text-center mt-6">
            <Link to="/coaches" className="text-primary hover:underline font-medium">
              View All Coaches →
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight uppercase text-foreground">
              How It Works
            </h2>
            <p className="font-serif text-lg text-muted-foreground mt-4 max-w-3xl mx-auto">
              Submit your practice, jackpot, or rodeo run and get it reviewed by proven professional athletes who understand what it takes to compete and win.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Record Your Run', desc: 'Film your practice, jackpot, or rodeo run on your phone. Clear video, 1 to 3 minutes works best.' },
              { step: '02', title: 'Submit for Professional Coaching', desc: 'Upload your video and tell us what you\'re working on. Your run goes directly to a professional coach.' },
              { step: '03', title: 'Get Expert Feedback', desc: 'Receive detailed coaching response you can watch and rewatch.' }
            ].map((item, i) => (
              <div key={i} className="text-center animate-fade-in" style={{ animationDelay: `${i * 100}ms` }}>
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                  <span className="font-display font-bold text-2xl text-primary">{item.step}</span>
                </div>
                <h3 className="font-display font-bold text-xl uppercase tracking-wide mb-3">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight uppercase text-foreground">
              Everything You Need
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <Card key={i} className="bg-white/80 backdrop-blur-sm border border-border shadow-sm hover:shadow-md transition-all duration-300 card-hover">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-display font-bold text-lg uppercase tracking-wide mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-background" id="pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight uppercase text-foreground">
              Simple Pricing
            </h2>
            <p className="font-serif text-lg text-muted-foreground mt-4 max-w-2xl mx-auto">
              Membership gets you in the door. Elevated Credits pay for coaching.
            </p>
          </div>

          {/* Subscription Plans */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
            {pricingPlans.map((plan, i) => (
              <Card key={i} className={`relative overflow-hidden ${plan.popular ? 'border-primary border-2 shadow-lg' : 'border-border'}`}>
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-primary text-primary-foreground px-4 py-1 text-xs font-bold uppercase rounded-bl-lg">
                    Best Value
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="font-display font-bold text-xl uppercase tracking-wide">{plan.name}</h3>
                  <div className="mt-4 flex items-baseline">
                    <span className="font-display font-black text-5xl text-foreground">{plan.price}</span>
                    <span className="text-muted-foreground ml-2">{plan.period}</span>
                  </div>
                  <p className="text-muted-foreground mt-2">{plan.description}</p>
                  <ul className="mt-6 space-y-3">
                    {plan.features.map((feature, j) => (
                      <li key={j} className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-secondary" />
                        <span className="text-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/signup">
                    <Button 
                      className={`w-full mt-8 rounded-full py-6 font-bold uppercase tracking-wide ${
                        plan.popular 
                          ? 'bg-primary hover:bg-primary/90 text-primary-foreground' 
                          : 'bg-muted hover:bg-muted/80 text-foreground'
                      }`}
                      data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
                    >
                      {plan.cta}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Credit Bundles */}
          <div className="text-center mb-8">
            <h3 className="font-display font-bold text-2xl uppercase tracking-wide">Elevated Credit Bundles</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {creditBundles.map((bundle, i) => (
              <Card key={i} className="text-center">
                <CardContent className="p-6">
                  <p className="font-display font-bold text-4xl text-primary">{bundle.credits}</p>
                  <p className="text-sm text-muted-foreground uppercase tracking-wide">Elevated Credits</p>
                  <p className="font-bold text-xl mt-3">{bundle.price}</p>
                  <p className="text-xs text-muted-foreground">{bundle.perCredit}/credit</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Become a Sponsor */}
      <section className="py-20 bg-primary/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                <Handshake className="w-8 h-8 text-primary" />
              </div>
              <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight uppercase text-foreground mb-6">
                Partner With Us
              </h2>
              <p className="font-serif text-xl text-muted-foreground mb-4">
                Join the growing community of brands supporting the next generation of ropers.
              </p>
              <p className="text-muted-foreground mb-6">
                Elevated Roping Media connects your brand with dedicated athletes and their families across North America. 
                Our platform reaches ropers at every level—from beginners learning the basics to professionals competing at the highest levels.
              </p>
              <ul className="space-y-3 text-muted-foreground mb-8">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-secondary flex-shrink-0" />
                  <span>Targeted exposure to the roping community</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-secondary flex-shrink-0" />
                  <span>Multiple partnership tiers to fit your goals</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-secondary flex-shrink-0" />
                  <span>Brand presence across digital platforms and events</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-secondary flex-shrink-0" />
                  <span>Authentic connection with western lifestyle enthusiasts</span>
                </li>
              </ul>
              <Link to="/sponsorship">
                <Button 
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-8 py-6 font-bold uppercase tracking-wide"
                >
                  View Partnership Packages
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="aspect-video rounded-2xl overflow-hidden shadow-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                <div className="text-center p-8">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_arena-coach-1/artifacts/zh1vb354_Elevated%20Logo.png" 
                    alt="Elevated Roping Media" 
                    className="h-24 w-auto mx-auto mb-6 opacity-80"
                  />
                  <p className="font-display font-bold text-2xl uppercase tracking-wide text-foreground/80">
                    Your Brand Here
                  </p>
                  <p className="text-muted-foreground mt-2">
                    Partner with the future of roping
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-bold text-2xl md:text-4xl tracking-tight uppercase text-foreground mb-4">
            Ready to Get Better?
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            Submit a video of your roping, and get honest, practical feedback you can actually use. No fluff, no gimmicks—just straightforward coaching from professional ropers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button 
                size="lg" 
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-10 py-6 font-bold uppercase tracking-wider"
              >
                Subscribe Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/#pricing">
              <Button 
                variant="outline"
                size="lg" 
                className="rounded-full px-10 py-6 font-bold uppercase tracking-wide border-2"
              >
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Elevated Roping Media */}
      <section className="py-12 bg-background border-t border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-muted-foreground uppercase tracking-wider mb-3">Brought to you by</p>
          <h3 className="font-display font-bold text-2xl uppercase tracking-wide text-foreground mb-4">
            Elevated Roping Media
          </h3>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Elevated Roping Media is dedicated to advancing the sport of roping through quality coaching, 
            authentic content, and tools that help ropers at every level improve their craft. 
            This is our flagship coaching platform—built by ropers, for ropers.
          </p>
        </div>
      </section>

      {/* Footer with Social Links */}
      <footer className="py-8 bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_arena-coach-1/artifacts/zh1vb354_Elevated%20Logo.png" 
                alt="Elevated Roping Media" 
                className="h-8 w-auto"
              />
              <span className="font-display font-bold tracking-tight text-background/90">ELEVATED ROPING MEDIA</span>
            </Link>
            
            {/* Social Media Links */}
            <div className="flex items-center gap-4">
              {socialLinks.map((social, i) => {
                const Icon = social.icon;
                return (
                  <a
                    key={i}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-white/20 hover:bg-primary hover:scale-110 flex items-center justify-center transition-all duration-200"
                    aria-label={social.label}
                    data-testid={`social-${social.label.toLowerCase()}`}
                  >
                    <Icon className="w-7 h-7 text-white" />
                  </a>
                );
              })}
            </div>
            
            <div className="text-center md:text-right">
              <p className="text-background/60 text-xs">
                © {new Date().getFullYear()} Elevated Roping Media. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Admin: Photo Position Editor Modal */}
      {editingCoachPhoto && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 relative">
            <button
              onClick={() => setEditingCoachPhoto(null)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-display font-bold text-lg uppercase mb-2">
              Adjust Photo: {editingCoachPhoto.name}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Drag to position, zoom to adjust how the photo appears on the landing page
            </p>
            <ThumbnailCropper
              image={editingCoachPhoto.photo_url}
              aspectRatio={3/4}
              onCropComplete={handleSavePhotoPosition}
              onCancel={() => setEditingCoachPhoto(null)}
              saving={savingPosition}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
